import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Plural from 'components/utils/Formatters/Plural'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import React, { Fragment, PropsWithChildren, ReactNode } from 'react'
import styled from 'styled-components'
import { nonNullable } from 'lib/array/arrayUtils'

export const AccommodationIndividualSelectRoomOptionTileDetailsContainer = styled(VerticalSpacer)``

type Props = {
  /**
   * positive integer
   */
  packageNumber?: number
  tagLineLabel?: ReactNode
  stayPayRateTier?: App.StayPayTier
  cancellationPolicy?: ReactNode
  variant: 'stacked' | 'carousel'
} & ({
  roomPackage?: App.HotelPackage
  selectedLoyaltyTargetUpgradePackage?: App.Package
  promotion?: undefined
} | {
  roomPackage?: undefined
  selectedLoyaltyTargetUpgradePackage?: undefined
  promotion?: App.BedbankPromotion
})

function AccommodationIndividualSelectRoomOptionTileDetails(props: PropsWithChildren<Props>) {
  const {
    children,
    packageNumber,
    tagLineLabel,
    roomPackage,
    promotion,
    stayPayRateTier,
    selectedLoyaltyTargetUpgradePackage,
    cancellationPolicy,
    variant,
  } = props

  const name = roomPackage?.name ?? promotion?.name
  const targetUpgradeName = selectedLoyaltyTargetUpgradePackage?.name
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const topLabels = nonNullable([
    packageNumber && variant === 'carousel' ? `Package option ${packageNumber}` : undefined,
    targetUpgradeName ? <b key="upgrade-label">{getLoyaltyTierDisplayName(displayTier)} member upgrade</b> : undefined,
  ])

  return <AccommodationIndividualSelectRoomOptionTileDetailsContainer gap={12}>
    <VerticalSpacer gap={4}>
      {topLabels.length > 0 && <Caption variant="large">
        {topLabels.map((label, i) => <Fragment key={i}>
          {i > 0 && ' | '}
          {label}
        </Fragment>)}
      </Caption>}
      {!!tagLineLabel && <LabelGroup>{tagLineLabel}</LabelGroup>}
      {!!targetUpgradeName && <BodyText variant="large" weight="bold">
        Upgraded to {targetUpgradeName}
      </BodyText>}
      {!!name && <BodyText
        variant="large"
        weight="bold"
        colour={selectedLoyaltyTargetUpgradePackage ? 'neutral-four' : 'neutral-one'}
        strikeThrough={!!selectedLoyaltyTargetUpgradePackage}
      >
        {name}
        {stayPayRateTier && <>, <Plural singular="night free" count={stayPayRateTier.freeNights} withCount /></>}
      </BodyText>}
      {cancellationPolicy}
    </VerticalSpacer>
    {children}
  </AccommodationIndividualSelectRoomOptionTileDetailsContainer>
}

export default AccommodationIndividualSelectRoomOptionTileDetails
